// Service pour mettre à jour le tracking du temps de jeu
import { supabase } from '@/integrations/supabase/client';
import { recalculatePlayerBadges, checkPlayPurchasedGamesMission } from './pointsService';

interface GamePlaytimeData {
	game_id: string;
	steam_game_id: number;
	playtime: number;
	playtime_2weeks: number;
}

// Mettre à jour la table playtime_tracking avec les données des jeux achetés
export async function updatePlaytimeTracking(playerId: string, games: GamePlaytimeData[]): Promise<void> {
	try {
		// Pour chaque jeu avec playtime
		for (const game of games) {
			// Vérifier si une entrée existe déjà avec key_request_id NULL
			const { data: existing } = await supabase
				.from('playtime_tracking')
				.select('id, steam_playtime')
				.eq('player_id', playerId)
				.eq('game_id', game.game_id)
				.is('key_request_id', null)
				.maybeSingle();

			const playtimeData = {
				player_id: playerId,
				game_id: game.game_id,
				key_request_id: null, // NULL pour les jeux achetés directement
				steam_playtime: game.playtime,
				required_playtime: 60, // 1 heure par défaut
				playtime_ratio: 1.0, // Si l'utilisateur joue, le ratio est à 1
				last_updated: new Date().toISOString()
			};

			if (existing) {
				// Mettre à jour l'entrée existante
				const { error: updateError } = await supabase
					.from('playtime_tracking')
					.update(playtimeData)
					.eq('id', existing.id);

				if (updateError) {
					// console.error('Erreur lors de la mise à jour:', updateError);
				} else {
					// console.log(`✅ Playtime mis à jour pour le jeu ${game.game_id}: ${game.playtime} min`);
				}
			} else {
				// Créer une nouvelle entrée
				const { error: insertError } = await supabase
					.from('playtime_tracking')
					.insert(playtimeData);

				if (insertError) {
					// console.error('Erreur lors de l\'insertion:', insertError);
				} else {
					// console.log(`✅ Playtime créé pour le jeu ${game.game_id}: ${game.playtime} min`);
				}
			}
		}

		// Recalculer les badges après mise à jour du playtime (affecte games_tested et total_playtime)
		// Faire en arrière-plan pour ne pas bloquer
		recalculatePlayerBadges(playerId).catch(error => {
			console.error('Error recalculating badges after playtime update:', error);
		});

		// Vérifier et compléter la mission "Play purchased games" si applicable
		// Faire en arrière-plan pour ne pas bloquer
		checkPlayPurchasedGamesMission(playerId).catch(error => {
			console.error('Error checking play purchased games mission:', error);
		});
	} catch (error) {
		// console.error('Erreur lors de la mise à jour du tracking:', error);
	}
}

// Récupérer le total playtime et le nombre de jeux testés
export async function getPlayerPlaytimeStats(playerId: string): Promise<{ total_playtime: number; games_tested: number }> {
	try {
		const { data, error } = await supabase
			.from('playtime_tracking')
			.select('steam_playtime')
			.eq('player_id', playerId);

		if (error) {
			// console.error('Erreur lors de la récupération des stats:', error);
			return { total_playtime: 0, games_tested: 0 };
		}

		const total_playtime = data?.reduce((sum, item) => sum + (item.steam_playtime || 0), 0) || 0;
		const games_tested = data?.length || 0;

		return { total_playtime, games_tested };
	} catch (error) {
		// console.error('Erreur lors de la récupération des stats:', error);
		return { total_playtime: 0, games_tested: 0 };
	}
}

